import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import type { WatchListing } from '../types/watch-monitor'
import { triggerSearch, getListings, dismissListing } from '../lib/watch-monitor-api'
import SearchResults from '../components/watch-monitor/SearchResults'
import WatchCatalog from '../components/watch-monitor/WatchCatalog'

type Tab = 'status' | 'results' | 'catalog'

const MARKETPLACES = [
  'Reddit r/watchexchange', 'eBay', 'Chrono24', 'Bezel', 'Hodinkee Shop',
  'Crown & Caliber', "Bob's Watches", 'Watchfinder', 'DavidSW',
  'Takuya Watches', 'Jomashop', 'Watchbox', 'Watches of Switzerland',
]

export default function WatchMonitorPage() {
  const [activeTab, setActiveTab] = useState<Tab>('status')
  const [listings, setListings] = useState<WatchListing[]>([])
  const [searching, setSearching] = useState(false)
  const [searchTimestamp, setSearchTimestamp] = useState<string | null>(null)

  // Load the most recent matched listings on mount so the Results tab shows
  // real data (with working links) without requiring a fresh search run.
  useEffect(() => {
    let cancelled = false
    getListings()
      .then((l) => { if (!cancelled) setListings(l) })
      .catch((err) => console.error('Failed to load listings:', err))
    return () => { cancelled = true }
  }, [])

  async function handleRunSearch() {
    try {
      setSearching(true)
      setActiveTab('results')
      const result = await triggerSearch()
      // The backend search endpoint returns only counts, so re-fetch the
      // stored matches to display the freshly-found listings.
      const fresh = await getListings()
      setListings(fresh)
      setSearchTimestamp(result.timestamp || new Date().toISOString())
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setSearching(false)
    }
  }

  // Optimistically remove the card, then persist the dismissal server-side so
  // it stays gone across re-scrapes and devices. Reload on failure to resync.
  async function handleRemove(listingId: string) {
    const previous = listings
    setListings((prev) => prev.filter((l) => l.listingId !== listingId))
    try {
      await dismissListing(listingId)
    } catch (err) {
      console.error('Failed to dismiss listing:', err)
      setListings(previous)
    }
  }

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'status', label: 'System Status' },
    { id: 'results', label: 'Search Results', count: listings.length || undefined },
    { id: 'catalog', label: 'Watch Catalog' },
  ]

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <span className="text-3xl">⌚</span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Watch Monitor</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Automated watch listing alert system</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative ${
                activeTab === tab.id
                  ? 'text-slate-900 dark:text-white bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 border-b-slate-800 -mb-px'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/70 dark:hover:bg-slate-800/50'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 px-1.5 py-0.5 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'status' && <StatusTab onRunSearch={handleRunSearch} />}
      {activeTab === 'results' && (
        <SearchResults
          listings={listings}
          loading={searching}
          onRunSearch={handleRunSearch}
          onRemove={handleRemove}
          searchTimestamp={searchTimestamp}
        />
      )}
      {activeTab === 'catalog' && <WatchCatalog />}
    </div>
  )
}

function StatusTab({ onRunSearch }: { onRunSearch: () => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Status card */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">System Status</h2>
          <button
            onClick={onRunSearch}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
          >
            Run Search Now
          </button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-slate-400">Lambda Function</span>
            <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-sm">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Active
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-slate-400">Schedule</span>
            <span className="text-slate-600 dark:text-slate-300 text-sm">4x daily (8AM, 12PM, 5PM, 10PM ET)</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-slate-400">Email From</span>
            <span className="text-slate-600 dark:text-slate-300 text-sm">alerts@nathanaelbrown.info</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-slate-500 dark:text-slate-400">Marketplaces</span>
            <span className="text-slate-600 dark:text-slate-300 text-sm">13+ sources</span>
          </div>
        </div>
      </div>

      {/* Monitored marketplaces */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Monitored Marketplaces</h2>
        <div className="grid grid-cols-2 gap-2">
          {MARKETPLACES.map((marketplace) => (
            <div key={marketplace} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {marketplace}
            </div>
          ))}
        </div>
      </div>

      {/* AWS Resources */}
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 lg:col-span-2">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">AWS Resources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: 'Lambda', resource: 'watch-monitor-checker', icon: 'λ' },
            { name: 'DynamoDB', resource: 'watch-monitor-seen-listings', icon: '🗄' },
            { name: 'EventBridge', resource: '4 schedule rules', icon: '⏰' },
            { name: 'SES', resource: 'nathanaelbrown.info', icon: '📧' },
          ].map((svc) => (
            <div key={svc.name} className="bg-slate-200/50 dark:bg-slate-700/50 rounded-lg p-4">
              <div className="text-xl mb-2">{svc.icon}</div>
              <p className="text-slate-900 dark:text-white font-medium text-sm">{svc.name}</p>
              <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">{svc.resource}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
